import {
    CoreMarker,
    CorePosition,
    CoreTextModel,
    LVGLSchemaService,
    XMLValidatorCore,
    XML_LANGUAGE_ID,
    parseXmlWithDomParser
} from '@lved/shared-core';
import { FrontendApplicationContribution } from '@theia/core/lib/browser';
import { DisposableCollection } from '@theia/core/lib/common/disposable';
import { inject, injectable } from '@theia/core/shared/inversify';
import * as monaco from '@theia/monaco-editor-core';
import { XmlSchemaContribution } from './xml-schema-contribution';

@injectable()
export class MonacoXmlValidationContribution implements FrontendApplicationContribution {
    @inject(XmlSchemaContribution)
    protected readonly xmlSchemaContribution!: XmlSchemaContribution;

    protected schemaService: LVGLSchemaService | undefined;
    protected unsubscribeFromSchemaHotReload: () => void = () => {};
    protected readonly validatedModels: WeakSet<monaco.editor.ITextModel> = new WeakSet<monaco.editor.ITextModel>();
    protected readonly toDispose: DisposableCollection = new DisposableCollection();

    async onStart(): Promise<void> {
        this.toDispose.push(
            this.xmlSchemaContribution.onDidChangeSchemaService((schemaService: LVGLSchemaService) => {
                this.setSchemaService(schemaService);
                this.validateAllModels();
            })
        );

        const schemaService: LVGLSchemaService | undefined = await this.xmlSchemaContribution.ensureInitialized();
        if (schemaService) this.setSchemaService(schemaService);

        this.toDispose.push(
            monaco.editor.onDidCreateModel((model: monaco.editor.ITextModel) => {
                this.attachValidation(model);
            })
        );

        for (const model of monaco.editor.getModels()) {
            this.attachValidation(model);
        }

        this.validateAllModels();
    }

    onStop(): void {
        this.unsubscribeFromSchemaHotReload();
        this.toDispose.dispose();
    }

    protected setSchemaService(schemaService: LVGLSchemaService): void {
        if (this.schemaService === schemaService) return;

        this.unsubscribeFromSchemaHotReload();
        this.schemaService = schemaService;
        this.unsubscribeFromSchemaHotReload = schemaService.onHotReload((): void => {
            this.validateAllModels();
        });
    }

    protected attachValidation(model: monaco.editor.ITextModel): void {
        if (model.isDisposed()) return;
        if (this.validatedModels.has(model)) return;
        this.validatedModels.add(model);

        const validate = (): void => {
            this.validateModel(model);
        };

        validate();

        const modelDisposables: DisposableCollection = new DisposableCollection();
        const contentListener: monaco.IDisposable = model.onDidChangeContent(() => validate());
        const languageListener: monaco.IDisposable = model.onDidChangeLanguage(() => validate());
        modelDisposables.push(contentListener);
        modelDisposables.push(languageListener);
        modelDisposables.push(
            model.onWillDispose(() => {
                monaco.editor.setModelMarkers(model, XML_LANGUAGE_ID, []);
                this.validatedModels.delete(model);
                modelDisposables.dispose();
            })
        );
        this.toDispose.push(modelDisposables);
    }

    protected validateAllModels(): void {
        for (const model of monaco.editor.getModels()) {
            this.attachValidation(model);
            this.validateModel(model);
        }
    }

    protected validateModel(model: monaco.editor.ITextModel): boolean {
        const modelUri: string = model.uri.toString();
        if (!modelUri.endsWith('.xml')) {
            monaco.editor.setModelMarkers(model, XML_LANGUAGE_ID, []);
            return true;
        }

        if (!this.schemaService) {
            monaco.editor.setModelMarkers(model, XML_LANGUAGE_ID, []);
            return true;
        }

        const coreModel: CoreTextModel = {
            getValue: () => model.getValue(),
            getLineCount: () => model.getLineCount(),
            getLineContent: (line: number) => model.getLineContent(line),
            getLineLength: (line: number) => model.getLineLength(line),
            getWordAtPosition: (position: CorePosition) => model.getWordAtPosition(position),
            getOffsetAt: (position: CorePosition) => model.getOffsetAt(position),
            uri: model.uri
        };

        const markers: CoreMarker[] = XMLValidatorCore(coreModel, this.schemaService, parseXmlWithDomParser);

        const monacoMarkers: monaco.editor.IMarkerData[] = markers.map((marker: CoreMarker) => ({
            ...marker,
            severity: monaco.MarkerSeverity.Error,
            startLineNumber: marker.startLineNumber,
            startColumn: marker.startColumn,
            endLineNumber: marker.endLineNumber,
            endColumn: marker.endColumn,
            message: marker.message
        }));

        monaco.editor.setModelMarkers(model, XML_LANGUAGE_ID, monacoMarkers);
        return monacoMarkers.length === 0;
    }
}
